import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { PiWarningCircle } from "react-icons/pi";
import classNames from "classnames";
import { Button } from "@/components/buttons";

/**
 * OverlayConfirmationModal — overlay-system replacement for the legacy
 * ModalConfirmation (Redux-based).  Receives all data as props (injected
 * by OverlayModal) instead of reading from `state.modal.options`.
 *
 * Usage:
 *   openModal("DELETE_CONFIRM", OverlayConfirmationModal, {
 *     title, description, textButtonOk, textButtonClose, mode, onConfirm,
 *   }, { size: OVERLAY_SIZES.SM });
 */
export default function OverlayConfirmationModal({
  handleClose,
  title,
  description,
  textButtonOk = "Confirm",
  textButtonClose = "Cancel",
  mode,
  onConfirm,
}) {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleOk = useCallback(async () => {
    if (!onConfirm) return;
    try {
      setIsProcessing(true);
      await onConfirm();
      handleClose?.();
    } catch {
      setIsProcessing(false);
    }
  }, [onConfirm, handleClose]);

  return (
    <div className="p-5">
      <div className="sm:flex sm:items-start">
        <div
          className={classNames(
            "mx-auto shrink-0 flex items-center justify-center h-12 w-12 sm:mx-0 sm:h-10 sm:w-10",
            { hidden: !mode },
          )}
        >
          <PiWarningCircle
            className={classNames("size-8 text-indigo-600", {
              "text-red-600": mode === "warning",
            })}
            aria-hidden="true"
          />
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-slate-100">
            {title}
          </h3>
          <div className="mt-2">
            <div className="text-sm text-gray-500 dark:text-slate-400">
              {description}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          label={textButtonOk}
          onClick={handleOk}
          isLoading={isProcessing}
          variant={mode === "warning" ? "danger" : "primary"}
          className="w-full sm:ml-3 sm:w-auto"
        />
        <Button
          label={textButtonClose}
          onClick={handleClose}
          variant="outline"
          className="mt-3 w-full sm:mt-0 sm:w-auto"
        />
      </div>
    </div>
  );
}

OverlayConfirmationModal.propTypes = {
  handleClose: PropTypes.func,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  textButtonOk: PropTypes.string,
  textButtonClose: PropTypes.string,
  mode: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
};

